import React, { useRef, useEffect, useState } from 'react';
import './Footer.css';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import InstallAppButton from './InstallAppButton';
import { useTechnologiesData } from '../context/TechnologiesContext';
import { useContactData } from '../context/ContactContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useSocieteData } from '../context/SocieteContext';
import pb from '../lib/pocketbase';

const TechSlider = () => {
  const trackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const animationRef = useRef(null);
  const widthRef = useRef(0); // Use ref for width access inside animation loop without deps

  // Vitesse de défilement automatique
  const autoSpeed = 0.5;

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const { technologies, loading } = useTechnologiesData();
  const activeTechs = technologies.filter(t => t.is_active !== false);

  useEffect(() => {
    if (trackRef.current) {
      widthRef.current = trackRef.current.scrollWidth / 2;
    }
    const handleResize = () => {
      if (trackRef.current) widthRef.current = trackRef.current.scrollWidth / 2;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeTechs, loading]); // Recalculate when techs load

  // Animation loop
  useEffect(() => {
    const animate = () => {
      if (!isDragging) {
        setCurrentTranslate(prev => {
          let newTranslate = prev - autoSpeed;
          const w = widthRef.current;
          if (w > 0 && Math.abs(newTranslate) >= w) {
            newTranslate = newTranslate + w;
          }
          return newTranslate;
        });
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [isDragging]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.clientX || (e.touches && e.touches[0].clientX));
    setPrevTranslate(currentTranslate);
    if (trackRef.current) trackRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    if (clientX === undefined) return;

    const diff = clientX - startX;
    let newTranslate = prevTranslate + diff;

    const w = widthRef.current;
    if (w > 0) {
      if (newTranslate > 0) newTranslate -= w;
      if (Math.abs(newTranslate) >= w) newTranslate += w;
    }
    setCurrentTranslate(newTranslate);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (trackRef.current) trackRef.current.style.cursor = 'grab';
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      if (trackRef.current) trackRef.current.style.cursor = 'grab';
    }
  };

  // Render tech item
  const renderTech = (tech, idx) => (
    <span key={`${tech.id}-${idx}`} className="tech-logo" title={tech.name}>
      {tech.logo ? (
        <img
          src={pb.files.getUrl(tech, tech.logo)}
          alt={tech.name}
          draggable={false}
          style={{
            width: '48px',
            height: '48px',
            objectFit: 'contain',
            mixBlendMode: !isDark ? 'multiply' : 'normal'
          }}
        />
      ) : (
        <span style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{tech.name}</span>
      )}
    </span>
  );

  if (loading) return <div className="tech-slider-mask">Chargement...</div>;

  const displayTechs = activeTechs.length > 0 ? activeTechs : [
    { id: 't1', name: 'React', logo: null },
    { id: 't2', name: 'PHP', logo: null },
    { id: 't3', name: '.NET', logo: null },
    { id: 't4', name: 'MySQL', logo: null }
  ];

  return (
    <div className="tech-slider-mask">
      <div
        className="tech-slider-track"
        ref={trackRef}
        style={{
          transform: `translateX(${currentTranslate}px)`,
          animation: 'none'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        onMouseMove={handleMouseMove}
        onTouchMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {/* We double the items for infinite scroll */}
        {displayTechs.map((t, i) => renderTech(t, `a-${i}`))}
        {displayTechs.map((t, i) => renderTech(t, `b-${i}`))}
      </div>
    </div>
  );
};


export default function Footer() {
  const year = new Date().getFullYear();
  const location = useLocation();
  const { sections, loading: loadingContact } = useContactData();
  const { showContact } = useSiteSettings();
  const { sections: societeSections } = useSocieteData();

  // Find info_form section to get contact details
  const contactSection = sections.find(s => s.type === 'info_form');
  const contactInfo = contactSection?.data || {};

  const displayAddress = contactInfo.address || "35 rue Pierre Nicole\n75005 Paris";
  const displayPhone = contactInfo.phone_info || "01 42 36 03 50";
  const displayEmail = contactInfo.email_info || "info@version-net.com";

  // Search for a map section slug in Societe for the anchor
  const societeMapSection = societeSections.find(s =>
    s.blocks && Array.isArray(s.blocks) && s.blocks.some(b => b.type === 'map')
  );
  const nousTrouverLink = showContact
    ? "/contact"
    : (societeMapSection ? `/societe#${societeMapSection.slug}` : "/societe");
  return (
    <footer className="site-footer">

      <div className="footer-inner">
        <div className="footer-brand">
          <Link to="/" className="footer-logo" aria-label="Version Net - Accueil" onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}>
            <img src="/images/logo.png" alt="Version Net" />
          </Link>
          <p className="footer-tagline">Solutions web & mobile.</p>
          <div style={{ marginTop: '0.5rem' }}>
            <InstallAppButton />
          </div>
        </div>

        <nav className="footer-nav">
          <div className="footer-col">
            <h4>Navigation</h4>
            <ul>
              <li><Link to="/">Accueil</Link></li>
              <li><Link to="/services">Services</Link></li>
              <li><Link to="/metiers">Métiers</Link></li>
              <li><Link to="/recrutement">Recrutement</Link></li>
              <li><Link to="/references">Références</Link></li>
              <li><Link to="/technologies">Technologies</Link></li>
              <li><Link to="/societe">Société</Link></li>
              {showContact && <li><Link to="/contact">Contact</Link></li>}
            </ul>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <ul>
              <li style={{ whiteSpace: 'pre-line', color: 'var(--text-muted)', fontSize: '0.95rem' }}>{displayAddress}</li>
              <li><a href={`tel:${displayPhone.replace(/\s/g, '')}`}>{displayPhone}</a></li>
              <li><a href={`mailto:${displayEmail}`}>{displayEmail}</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>Nous trouver</h4>
            <div style={{ marginTop: '0.5rem' }}>
              <Link to={nousTrouverLink} style={{ display: 'block', transition: 'transform 0.2s' }} className="footer-map-link">
                <img
                  src="/images/map.png"
                  alt="Plan d'accès Version Net - Cliquez pour voir où nous sommes"
                  style={{
                    width: '100%',
                    maxWidth: '240px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-subtle)',
                    display: 'block',
                    transition: 'transform 0.2s, filter 0.2s'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.filter = 'brightness(1.1)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.filter = 'brightness(1)'; }}
                />
              </Link>
            </div>
          </div>

          <div className="footer-col footer-tech">
            <h4>Technologies</h4>
            <TechSlider />
          </div>
        </nav>
      </div>

      <div className="footer-bottom">
        <p className="footer-copyright">© {year} Version Net. Tous droits réservés.</p>

        <p className="footer-powered-by">
          Site propulsé et réalisé par <a href="https://www.infocolab.com/" target="_blank" rel="noopener noreferrer">Infocolab</a>
        </p>

        <div className="footer-legal">
          <Link to="/mentions-legales">Mentions légales & Confidentialité</Link>
        </div>
      </div>
    </footer >
  );
}
